import { useState, useMemo, useEffect, useRef } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { InsightsHudBar } from '@/components/analytics/InsightsHudBar';
import { InsightsVitalsSidebar } from '@/components/analytics/InsightsVitalsSidebar';
import { InsightsPulseChart } from '@/components/analytics/InsightsPulseChart';
import { InsightsCoachPanel } from '@/components/analytics/InsightsCoachPanel';
import { PaceZoneChart } from '@/components/analytics/PaceZoneChart';

import { ComplianceDashboard } from '@/components/calendar/ComplianceDashboard';
import { GarminDerivedAttribution } from '@/components/activities/GarminAttribution';
import { AppleHealthDerivedAttribution } from '@/components/activities/AppleHealthAttribution';
import { cn } from '@/lib/utils';
import {
  fetchActivities,
  fetchOverview,
  getIntegrationsStatus,
  fetchTrainingLoad,
  fetchPerformanceAnalytics,
  backfillRecentActivityStreams,
  fetchInsightsComplianceAdvanced,
  fetchRiskAssessment,
} from '@/lib/api';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { CompletedActivity, TrainingLoad } from '@/types';
import { useSyncTodayWorkout } from '@/hooks/useSyncTodayWorkout';
import { PlanCoachChat } from '@/components/plan/PlanCoachChat';
import { log } from '@/lib/logger';

// New analytics components
import { RampRateCard } from '@/components/analytics/RampRateCard';
import { CTLProjectionChart } from '@/components/analytics/CTLProjectionChart';
import { WeeklyTSSChart } from '@/components/analytics/WeeklyTSSChart';
import { AnnualHeatmap } from '@/components/analytics/AnnualHeatmap';
import { RiskPanel } from '@/components/analytics/RiskPanel';
import { PowerDurationCurve } from '@/components/analytics/PowerDurationCurve';
import { PeakPowerTable } from '@/components/analytics/PeakPowerTable';
import { EfficiencyFactorChart } from '@/components/analytics/EfficiencyFactorChart';
import { ZoneDistributionChart } from '@/components/analytics/ZoneDistributionChart';
import { WorkoutComplianceChart } from '@/components/analytics/WorkoutComplianceChart';
import { YoYPMCChart } from '@/components/analytics/YoYPMCChart';

// Closed-loop analytics components
import { ClosedLoopPMCChart } from '@/components/insights/ClosedLoopPMCChart';
import { AdherenceCard } from '@/components/insights/AdherenceCard';
import { ClosedLoopZoneChart } from '@/components/insights/ClosedLoopZoneChart';
import { FitnessProjectionChart } from '@/components/insights/FitnessProjectionChart';
import { ClosedLoopRiskPanel } from '@/components/insights/ClosedLoopRiskPanel';
import { TrendIndicators } from '@/components/insights/TrendIndicators';
import { MetricInfoButton } from '@/components/MetricInfoButton';
import { SportDashboards } from '@/components/analytics/SportDashboards';

type MetricEntry = { date: string; value: number };

const toFiniteNumber = (value: unknown): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeMetricSeries = (series: unknown, metricKey: string): MetricEntry[] => {
  if (!Array.isArray(series)) return [];

  return series
    .map((item): MetricEntry | null => {
      if (Array.isArray(item) && item.length >= 2) {
        const date = typeof item[0] === 'string' ? item[0] : '';
        if (!date) return null;
        return { date, value: toFiniteNumber(item[1]) };
      }

      if (!item || typeof item !== 'object') return null;

      const source = item as Record<string, unknown>;
      const date = typeof source.date === 'string'
        ? source.date
        : typeof source.day === 'string'
          ? source.day
          : '';
      if (!date) return null;

      const value = source[metricKey] ?? source.value ?? source.y;
      return { date, value: toFiniteNumber(value) };
    })
    .filter((entry): entry is MetricEntry => entry !== null);
};

export type InsightsDateRange = '7d' | '30d' | '60d' | '90d' | 'all';

const INSIGHTS_OVERVIEW_DAYS = 365;

const loadAllActivities = async (): Promise<CompletedActivity[]> => {
  const pageSize = 1000;
  const maxPages = 20;
  let offset = 0;
  let page = 0;
  const merged: CompletedActivity[] = [];

  while (page < maxPages) {
    const batch = await fetchActivities({ limit: pageSize, offset });
    if (batch.length === 0) break;
    merged.push(...batch);
    if (batch.length < pageSize) break;
    offset += pageSize;
    page += 1;
  }

  const uniqueById = new Map<string, CompletedActivity>();
  for (const activity of merged) uniqueById.set(activity.id, activity);
  return [...uniqueById.values()];
};

export default function Analytics() {
  useSyncTodayWorkout();
  const [dateRange, setDateRange] = useState<InsightsDateRange>('90d');
  const [dateRangeAutoSet, setDateRangeAutoSet] = useState(false);
  const queryClient = useQueryClient();
  const streamBackfillTriggeredRef = useRef(false);

  // ─── Existing data fetches ───────────────────────────────────
  const { data: overview, isLoading, error } = useAuthenticatedQuery({
    queryKey: ['overview', INSIGHTS_OVERVIEW_DAYS],
    queryFn: () => fetchOverview(INSIGHTS_OVERVIEW_DAYS),
    retry: 1,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    gcTime: 5 * 60 * 1000,
  });

  // Auto-set default range based on user's history length (once)
  const historyDays = useMemo(() => {
    const metrics = overview?.metrics as Record<string, unknown> | undefined;
    if (!metrics) return undefined;
    const ctlSeries = normalizeMetricSeries(metrics.ctl, 'ctl');
    const chartSeries = ctlSeries.length > 0 ? ctlSeries : normalizeMetricSeries(metrics.chart, 'CTL');
    return chartSeries.length;
  }, [overview]);

  useEffect(() => {
    if (dateRangeAutoSet || historyDays === undefined) return;
    setDateRangeAutoSet(true);
    if (historyDays < 30) setDateRange('7d');
    else if (historyDays < 60) setDateRange('30d');
    else if (historyDays < 90) setDateRange('60d');
    // else keep default 90d
  }, [historyDays, dateRangeAutoSet]);

  const { data: overview60d, isLoading: overview60dLoading } = useAuthenticatedQuery({
    queryKey: ['overview', 60],
    queryFn: () => fetchOverview(60),
    retry: 1,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const { data: integrationsStatus } = useAuthenticatedQuery({
    queryKey: ['integrations', 'status'],
    queryFn: getIntegrationsStatus,
    staleTime: 2 * 60 * 1000,
  });

  const { data: allActivitiesForBackfill = [] } = useAuthenticatedQuery({
    queryKey: ['insights', 'pace-zones', 'activities'],
    queryFn: loadAllActivities,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // ─── New advanced data fetches ───────────────────────────────
  const { data: trainingLoad } = useAuthenticatedQuery({
    queryKey: ['training-load', 365],
    queryFn: () => fetchTrainingLoad(365),
    staleTime: 120_000,
  });

  const { data: performanceAnalytics } = useAuthenticatedQuery({
    queryKey: ['analytics', 'performance', 90],
    queryFn: () => fetchPerformanceAnalytics(90),
    staleTime: 120_000,
  });

  const { data: complianceData } = useAuthenticatedQuery({
    queryKey: ['insights', 'compliance-advanced', 12],
    queryFn: () => fetchInsightsComplianceAdvanced(12),
    staleTime: 120_000,
  });

  const { data: riskAssessment, isLoading: riskAssessmentLoading } = useAuthenticatedQuery({
    queryKey: ['state', 'risk-assessment', 90],
    queryFn: () => fetchRiskAssessment(90),
    staleTime: 60_000,
  });

  // ─── Derived data ────────────────────────────────────────────
  const ctlArray = useMemo(() => trainingLoad?.ctl ?? [], [trainingLoad]);
  const atlArray = useMemo(() => trainingLoad?.atl ?? [], [trainingLoad]);
  const tsbArray = useMemo(() => trainingLoad?.tsb ?? [], [trainingLoad]);
  const ctlArrayRaw = useMemo(() => trainingLoad?.ctl_raw ?? ctlArray, [trainingLoad, ctlArray]);
  const atlArrayRaw = useMemo(() => trainingLoad?.atl_raw ?? atlArray, [trainingLoad, atlArray]);
  const tsbArrayRaw = useMemo(() => trainingLoad?.tsb_raw ?? tsbArray, [trainingLoad, tsbArray]);
  const dates = useMemo(() => trainingLoad?.dates ?? [], [trainingLoad]);
  const dailyTss = useMemo(() => trainingLoad?.daily_tss ?? [], [trainingLoad]);
  const rampRateCtlPerWeek = useMemo(
    () => (typeof trainingLoad?.ramp_rate_ctl_per_week === 'number' ? trainingLoad.ramp_rate_ctl_per_week : undefined),
    [trainingLoad],
  );

  const historicalPMC = useMemo(
    () =>
      dates.map((d, i) => ({
        date: d,
        ctl: ctlArrayRaw[i] ?? 0,
        atl: atlArrayRaw[i] ?? 0,
        tsb: tsbArrayRaw[i] ?? 0,
      })),
    [dates, ctlArrayRaw, atlArrayRaw, tsbArrayRaw],
  );

  const efData = useMemo(
    () =>
      (performanceAnalytics?.efficiency_metrics?.series ?? []).map((s) => ({
        date: s.date,
        ef: s.ef,
        aerobicDecoupling: s.aerobic_decoupling,
        intensityFactor: s.intensity_factor ?? undefined,
        variabilityIndex: s.variability_index ?? undefined,
      })),
    [performanceAnalytics],
  );

  const peakPowerEntries = useMemo(
    () =>
      (performanceAnalytics?.peak_power_records?.peaks ?? []).map((p) => ({
        duration: p.duration,
        durationSeconds: p.duration_seconds,
        power: p.power,
        date: p.date,
        activityName: p.activity_name,
      })),
    [performanceAnalytics],
  );

  const hasPowerData = (performanceAnalytics?.power_duration_curve?.peaks?.length ?? 0) > 0;

  const hasAdvancedData = ctlArray.length > 0;

  useEffect(() => {
    if (streamBackfillTriggeredRef.current) return;
    const audit = performanceAnalytics?.ingestion_audit;
    if (!audit) return;

    const total = Number(audit.totals?.activities ?? 0);
    const withoutStreams = Number(audit.totals?.without_streams ?? 0);
    if (total <= 0 || withoutStreams <= 0) return;

    const throttleKey = 'analytics_stream_backfill_last_run';
    const now = Date.now();
    const lastRun = Number(sessionStorage.getItem(throttleKey) ?? 0);
    if (Number.isFinite(lastRun) && now - lastRun < 15 * 60 * 1000) return;

    const candidates = [...allActivitiesForBackfill]
      .filter((activity) => (activity.source === 'strava' || activity.source === 'garmin') && activity.hasStreams !== true)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 5)
      .map((activity) => activity.id);

    if (!candidates.length) return;

    streamBackfillTriggeredRef.current = true;
    sessionStorage.setItem(throttleKey, String(now));

    void backfillRecentActivityStreams(candidates)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['analytics', 'performance', 90] });
      })
      .catch((error) => {
        log.warn('ANALYTICS', 'Stream backfill failed:', error);
      });
  }, [performanceAnalytics, allActivitiesForBackfill, queryClient]);

  const garminConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'garmin' && i.connected
  ) ?? false;
  const appleHealthConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'apple_health' && i.connected
  ) ?? false;

  const filteredData = useMemo((): TrainingLoad[] => {
    const metrics = overview?.metrics as Record<string, unknown> | undefined;
    if (!metrics) return [];

    let ctlSeries = normalizeMetricSeries(metrics.ctl, 'ctl');
    const atlSeries = normalizeMetricSeries(metrics.atl, 'atl');
    const tsbSeries = normalizeMetricSeries(metrics.tsb, 'tsb');
    const tssSeries = normalizeMetricSeries(metrics.tss, 'tss');

    if (ctlSeries.length === 0 && Array.isArray(metrics.chart)) {
      ctlSeries = normalizeMetricSeries(metrics.chart, 'CTL');
    }

    if (ctlSeries.length === 0) return [];

    const atlByDate = new Map(atlSeries.map((entry) => [entry.date, entry.value]));
    const tsbByDate = new Map(tsbSeries.map((entry) => [entry.date, entry.value]));
    const tssByDate = new Map(tssSeries.map((entry) => [entry.date, entry.value]));

    const fullSeries = ctlSeries.map((entry) => ({
      date: entry.date,
      ctl: entry.value,
      atl: atlByDate.get(entry.date) ?? 0,
      tsb: tsbByDate.get(entry.date) ?? 0,
      dailyLoad: tssByDate.get(entry.date) ?? 0,
    }));

    if (dateRange === 'all') return fullSeries;
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '60d' ? 60 : 90;
    return fullSeries.slice(-days);
  }, [overview, dateRange]);

  const todayMetrics = {
    ctl: toFiniteNumber(overview?.today?.ctl),
    atl: toFiniteNumber(overview?.today?.atl),
    tsb: toFiniteNumber(overview?.today?.tsb),
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (error || !overview) {
    return (
      <AppLayout>
        <div className="text-center py-12 text-muted-foreground">
          <p>Unable to load insights data</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <PageHeader
            title="Insights"
            subtitle="System diagnostic · command center"
            statusLabel="DIAGNOSTICS: LIVE"
            status="green"
          />
          <div className="flex items-center gap-1">
            {(['7d', '30d', '60d', '90d', 'all'] as InsightsDateRange[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setDateRange(r)}
                className={cn(
                  'px-3 py-1.5 rounded text-xs font-mono tracking-wider uppercase transition-all border',
                  dateRange === r
                    ? 'bg-[rgba(0,255,200,0.12)] text-[#00FFC8] border-[rgba(0,255,200,0.3)]'
                    : 'text-muted-foreground border-white/[0.06] hover:border-white/[0.12]'
                )}
              >
                {r === 'all' ? 'All' : r}
              </button>
            ))}
          </div>
        </div>

        {/* System HUD Bar */}
        <InsightsHudBar
          ctl={todayMetrics.ctl}
          atl={todayMetrics.atl}
          tsb={todayMetrics.tsb}
          riskBand={riskAssessment?.risk_band}
        />

        {/* 3-Column HUD Layout (Desktop) / Stacked (Mobile) */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-4">
          {/* Column 1: Vitals Sidebar */}
          <InsightsVitalsSidebar
            data={filteredData}
            overview60d={overview60d}
            className="hidden lg:block"
          />

          {/* Column 2: Performance Signal */}
          <div className="space-y-4">
            <InsightsPulseChart data={filteredData} />
            <PaceZoneChart
              paceZones={performanceAnalytics?.zone_distribution?.pace_zones ?? []}
              zoneDefinitions={performanceAnalytics?.pace_zone_definitions}
              thresholdPaceSource={performanceAnalytics?.threshold_pace_source}
            />
            <ComplianceDashboard showWeekly={true} showSeason={true} />
          </div>

          {/* Column 3: Coach Analysis */}
          <div className="space-y-4">
            <InsightsCoachPanel data={filteredData} />
            <InsightsVitalsSidebar
              data={filteredData}
              overview60d={overview60d}
              className="lg:hidden"
            />
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            ADVANCED ANALYTICS — TrainingPeaks parity
            ═══════════════════════════════════════════════════════════ */}
        
        {hasAdvancedData && (
          <div className="space-y-5 pt-4 border-t border-border/30">
            <h2 className="text-[11px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
              ▸ Advanced Performance Analytics
            </h2>

            {/* Risk & Readiness KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <RampRateCard ctlArray={ctlArrayRaw} rampRateOverride={rampRateCtlPerWeek} />
              <div className="rounded-xl border border-border/60 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Current CTL</div>
                  <MetricInfoButton metricKey="ctl" />
                </div>
                <div className="text-2xl font-bold tabular-nums text-foreground">
                  {todayMetrics.ctl.toFixed(1)}
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">Aerobic Base</div>
              </div>
              <div className="rounded-xl border border-border/60 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Current TSB</div>
                  <MetricInfoButton metricKey="tsb" />
                </div>
                <div className={`text-2xl font-bold tabular-nums ${todayMetrics.tsb > 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {todayMetrics.tsb.toFixed(1)}
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">Freshness Index</div>
              </div>
            </div>

            <RiskPanel
              risk={riskAssessment}
              loading={riskAssessmentLoading}
              fallbackMetrics={{
                rampRateCtlPerWeek: rampRateCtlPerWeek,
                tsb: todayMetrics.tsb,
              }}
            />

            {/* Projection & Weekly TSS */}
            <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <CTLProjectionChart historicalData={historicalPMC} />
              <WeeklyTSSChart
                dates={dates}
                dailyTss={dailyTss}
                weeklyDates={trainingLoad?.weekly_tss_dates}
                weeklyTss={trainingLoad?.weekly_tss}
              />
            </section>

            {/* YoY Comparison */}
            <YoYPMCChart data={historicalPMC} />

            {/* Annual Heatmap */}
            <AnnualHeatmap dates={dates} values={dailyTss} />

            {/* Power & Running Analytics */}
            <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {hasPowerData && (
                <PowerDurationCurve peaks={performanceAnalytics?.power_duration_curve?.peaks ?? []} />
              )}
              <ZoneDistributionChart
                powerZones={performanceAnalytics?.zone_distribution?.power_zones}
                hrZones={performanceAnalytics?.zone_distribution?.hr_zones}
              />
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {hasPowerData && <PeakPowerTable peaks={peakPowerEntries} />}
              <EfficiencyFactorChart data={efData} />
            </section>

            {/* Compliance */}
            {(complianceData?.series?.length ?? 0) > 0 && (
              <WorkoutComplianceChart data={complianceData!.series} />
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            SPORT-SPECIFIC ANALYTICS — Phase 5 multi-sport dashboards
            ═══════════════════════════════════════════════════════════ */}
        <SportDashboards activities={allActivitiesForBackfill} />

        {/* ═══════════════════════════════════════════════════════════
            CLOSED-LOOP ANALYTICS — Engine-powered intelligence
            ═══════════════════════════════════════════════════════════ */}
        <div className="space-y-5 pt-4 border-t border-border/30">
          <h2 className="text-[11px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
            ▸ Closed-Loop Intelligence
          </h2>

          {/* Trend indicators row */}
          <TrendIndicators />

          {/* PMC + Adherence side-by-side */}
          <section className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
            <ClosedLoopPMCChart />
            <AdherenceCard />
          </section>

          {/* Projection + Zone distribution */}
          <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <FitnessProjectionChart />
            <ClosedLoopZoneChart />
          </section>

          {/* Risk panel */}
          <ClosedLoopRiskPanel />
        </div>

        {garminConnected && (
          <div className="pt-4 border-t border-border/50">
            <GarminDerivedAttribution />
          </div>
        )}
        {appleHealthConnected && (
          <div className="pt-4 border-t border-border/50">
            <AppleHealthDerivedAttribution />
          </div>
        )}

        <PlanCoachChat />

        {/* Bottom spacer for mobile bottom nav */}
        <div className="h-20 lg:hidden" />
      </div>
    </AppLayout>
  );
}
